import questions from "../onboardingFlow/questions.json";

export function clearNextSteps(formData, fromId) {
  const idsToDelete = new Set();

  function traverseNext(id) {
    const current = formData[id];
    if (!current || !current.navigation) return;

    const nav = current.navigation;

    // Simple next
    if (nav.next) {
      idsToDelete.add(nav.next);
      traverseNext(nav.next);
    }

    // Conditional next
    if (nav.type === "conditional" && Array.isArray(nav.conditions)) {
      nav.conditions.forEach((condition) => {
        if (condition.next) {
          idsToDelete.add(condition.next);
          traverseNext(condition.next);
        }
      });
    }
  }

  traverseNext(fromId);

  const cleanedData = { ...formData };

  idsToDelete.forEach((id) => {
    delete cleanedData[id];
  });

  return cleanedData;
}

function findStartQuestionId(formData) {
  return Object.keys(formData).find(
    (id) => formData[id]?.navigation?.previous === null
  );
}

function getNextQuestionId(question) {
  const nav = question.navigation;
  if (!nav) return null;

  // Conditional navigation
  if (nav.type === "conditional" && Array.isArray(nav.conditions)) {
    const selectedValue = question.value;
    const match = nav.conditions.find((c) => c.when === selectedValue);
    return match?.next ?? null;
  }

  // Linear navigation
  return nav.next ?? null;
}

export function getOrderedQuestionList(storeAnswers) {
  const ordered = [];

  let startQuestionId = questions.startScreen;
  const visited = new Set();

  while (startQuestionId && !visited.has(startQuestionId)) {
    visited.add(startQuestionId);

    const question = storeAnswers[startQuestionId];
    if (!question) break;

    ordered.push({
      id: startQuestionId,
      ...question,
    });

    startQuestionId = getNextQuestionId(question);
  }

  return ordered;
}

export function getAllPossiblePaths(screens, startId) {
  const paths = [];

  function dfs(currentId, path, visited) {
    if (!currentId || currentId === "end") {
      paths.push([...path]);
      return;
    }

    if (visited.has(currentId)) return;

    visited.add(currentId);
    path.push(currentId);

    const screen = screens[currentId];
    if (!screen || !screen.navigation) {
      paths.push([...path]);
    } else {
      const nav = screen.navigation;

      // Conditional navigation
      if (nav.type === "conditional") {
        nav.conditions.forEach((c) => {
          dfs(c.next, path, new Set(visited));
        });
      }
      // Linear navigation
      else if (nav.next) {
        dfs(nav.next, path, new Set(visited));
      }
    }

    path.pop();
  }

  dfs(startId, [], new Set());
  return paths;
}

export function getShortestPath(paths) {
  return paths.reduce((shortest, path) =>
    path.length < shortest.length ? path : shortest
  );
}

export function findMatchingPath(paths, answers, screens) {
  return paths.find((path) => {
    for (let i = 0; i < path.length; i++) {
      const qId = path[i];
      const screen = screens[qId];
      const answer = answers[qId]?.value;

      if (screen?.navigation?.type === "conditional" && answer) {
        const expectedNext = screen.navigation.conditions.find(
          (c) => c.when === answer
        )?.next;

        if (path[i + 1] !== expectedNext) {
          return false;
        }
      }
    }
    return true;
  });
}

// this is ai generated code
export function getStepInfoUsingPaths({
  screens,
  answers,
  startId,
  currentQuestionId,
}) {
  // Build path forward from start to current question using next links and answers
  // This ensures we follow the actual path the user took based on their answers
  function buildPathForward(fromId, toId) {
    const path = [];
    const visited = new Set();
    let current = fromId;

    while (current && !visited.has(current)) {
      visited.add(current);
      path.push(current);

      // Stop if we've reached the target question
      if (current === toId) {
        break;
      }

      const screen = screens[current];
      if (!screen?.navigation) break;

      const nav = screen.navigation;

      // Determine next question based on navigation type and answers
      let nextId = null;

      // Conditional navigation - use answer to determine next
      if (nav.type === "conditional" && Array.isArray(nav.conditions)) {
        const answer = answers[current]?.value;
        if (answer) {
          const match = nav.conditions.find((c) => c.when === answer);
          nextId = match?.next ?? null;
        } else {
          // If no answer yet, we can't determine the path forward
          break;
        }
      }
      // Linear navigation
      else if (nav.next) {
        nextId = nav.next;
      }

      if (!nextId || nextId === "end") break;

      current = nextId;
    }

    return path;
  }

  // Get the path from start to current (forward path based on actual answers)
  const pathToCurrent = buildPathForward(startId, currentQuestionId);

  // Get the full path using path matching algorithm for total steps
  const paths = getAllPossiblePaths(screens, startId);
  const shortestPath = getShortestPath(paths);
  const matchedPath = findMatchingPath(paths, answers, screens);
  const fullPath = matchedPath || shortestPath;

  // Calculate current step from the path to current (which always includes currentQuestionId)
  const currentStep = pathToCurrent.length;

  // Calculate total steps from the full path
  // If current question is in full path, use that; otherwise use pathToCurrent length
  const totalSteps = fullPath.includes(currentQuestionId)
    ? fullPath.length
    : Math.max(fullPath.length, pathToCurrent.length);

  return {
    currentStep: currentStep > 0 ? currentStep : 1,
    totalSteps: totalSteps,
  };
}
